import { useState } from 'react';
import { motion } from 'framer-motion';
import { Calculator, CircleDot } from 'lucide-react';
import type { ApartmentType } from '@pullman/shared';
import { useTranslation } from '@/i18n';

function formatAMD(value: number): string {
  return new Intl.NumberFormat('en-US').format(Math.round(value));
}

function formatUSD(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

interface PaymentCalculatorProps {
  property: ApartmentType;
}

export function PaymentCalculator({ property }: PaymentCalculatorProps) {
  const { t } = useTranslation();
  const [showUSD, setShowUSD] = useState(false);
  const [selectedTier, setSelectedTier] = useState(0);

  if (!property.pricing.length) {
    return (
      <motion.div
        className="bg-white rounded-card shadow-lg overflow-hidden border border-gray-100 p-6 text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Calculator size={24} className="text-brand-gold mx-auto mb-2" />
        <p className="text-sm text-brand-charcoal/60">{t('payment.contactSales')}</p>
      </motion.div>
    );
  }

  const tier = property.pricing[selectedTier];
  const totalAMD = tier.price_amd;
  const totalUSD = tier.price_usd_approx;

  // 30% down, 2% monthly x 30, 10% final
  const downPayment = totalAMD * 0.3;
  const monthlyPayment = totalAMD * 0.02;
  const totalMonthly = monthlyPayment * 30;
  const finalPayment = totalAMD * 0.1;

  const downPaymentUSD = totalUSD * 0.3;
  const monthlyPaymentUSD = totalUSD * 0.02;
  const finalPaymentUSD = totalUSD * 0.1;

  return (
    <motion.div
      className="bg-white rounded-card shadow-lg overflow-hidden border border-gray-100"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, type: 'spring', damping: 20, stiffness: 200 }}
    >
      <div className="bg-brand-navy p-4">
        <div className="flex items-center gap-2">
          <Calculator size={18} className="text-brand-gold" />
          <h3 className="font-display text-sm tracking-wider text-white uppercase">
            {t('payment.title')} — {property.name}
          </h3>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Tier selector */}
        {property.pricing.length > 1 && (
          <div className="flex flex-wrap gap-2">
            {property.pricing.map((pt, i) => (
              <button
                key={pt.floor_range}
                onClick={() => setSelectedTier(i)}
                className={`px-3 py-1 text-xs rounded-full transition-all ${
                  selectedTier === i
                    ? 'bg-brand-navy text-white'
                    : 'border border-gray-200 text-brand-charcoal/70 hover:border-brand-gold/50'
                }`}
              >
                {pt.floor_range}
              </button>
            ))}
          </div>
        )}

        {/* Total */}
        <div className="text-center pb-2 border-b border-gray-100">
          <p className="text-xs text-brand-charcoal/50 uppercase tracking-wide">{t('payment.totalPrice')}</p>
          <p className="text-xl font-semibold text-brand-charcoal mt-1">
            {showUSD ? formatUSD(totalUSD) : `${formatAMD(totalAMD)} AMD`}
          </p>
        </div>

        {/* Breakdown */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-brand-charcoal/70">{t('payment.downPayment')}</span>
            <span className="text-sm font-semibold">
              {showUSD ? formatUSD(downPaymentUSD) : `${formatAMD(downPayment)} AMD`}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-brand-charcoal/70">{t('payment.monthly')}</span>
            <span className="text-sm font-semibold">
              {showUSD ? formatUSD(monthlyPaymentUSD) : `${formatAMD(monthlyPayment)} AMD`}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-brand-charcoal/70">{t('payment.finalPayment')}</span>
            <span className="text-sm font-semibold">
              {showUSD ? formatUSD(finalPaymentUSD) : `${formatAMD(finalPayment)} AMD`}
            </span>
          </div>
        </div>

        {/* Timeline */}
        <div className="pt-3 border-t border-gray-100 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-charcoal/50">
            {t('payment.timeline')}
          </p>
          <div className="space-y-2 text-sm text-brand-charcoal/70">
            <div className="flex items-center gap-2">
              <CircleDot size={14} className="text-brand-gold flex-shrink-0" />
              <span>{t('payment.today')}</span>
            </div>
            <div className="flex items-center gap-2">
              <CircleDot size={14} className="text-brand-gold/60 flex-shrink-0" />
              <span>{t('payment.monthlyInstallments')} {showUSD ? formatUSD(monthlyPaymentUSD) : `${formatAMD(monthlyPayment)} AMD`}</span>
            </div>
            <div className="flex items-center gap-2">
              <CircleDot size={14} className="text-brand-gold/40 flex-shrink-0" />
              <span>{t('payment.handover')}</span>
            </div>
          </div>
        </div>

        {/* Toggle currency */}
        <div className="flex gap-2 pt-2">
          <button
            onClick={() => setShowUSD(!showUSD)}
            className="flex-1 px-3 py-2 text-sm rounded-full
              border border-brand-gold/30 text-brand-charcoal
              hover:bg-brand-gold/10 hover:border-brand-gold/50 transition-all"
          >
            {showUSD ? t('payment.seeInAMD') : t('payment.seeInUSD')}
          </button>
        </div>

        {/* Disclaimer */}
        <p className="text-[11px] text-brand-charcoal/40 text-center leading-relaxed">
          {t('payment.disclaimer')}
        </p>
      </div>
    </motion.div>
  );
}
